import Timesheet from "../models/Timesheet";
import Utils from "../utils/Utils";
import ApiService from "./ApiService";
import RepositoryTeammemberCommit from "../viewModels/response/repository/RepositoryTeammemberCommit";

export default class TimesheetService {
  public static async getById(id: string): Promise<Timesheet | null> {
    return new Promise((resolve, reject) => {
      ApiService.post(`/timesheet/getById/${id}`, "")
        .then((result) => {
          if (result.data) {
            resolve({ id: result.data.id, ...result.data } as Timesheet);
          } else {
            resolve(null);
          }
        })
        .catch((error) => reject(error));
    });
  }

  public static async canEdit(model: Timesheet): Promise<string> {
    return new Promise((resolve, reject) => {
      ApiService.post("/timesheet/canEdit", model)
        .then((result) => {
          resolve(result.data);
        })
        .catch((error) => reject(error));
    });
  }

  public static async saveTimesheet(model: Timesheet): Promise<string> {
    (model.Date as any) = Utils.getDateString(model.Date);
    return new Promise((resolve, reject) => {
      ApiService.post("/timesheet/saveforadmin", model)
        .then((result) => {
          resolve(result.data);
        })
        .catch((error) => reject(error));
    });
  }

  public static async updateAdjustment(id: string, adjustment: any, isAdmin: boolean): Promise<void> {
    let url = "";
    if (isAdmin) {
      url = `/timesheet/admin/updateAdjustment`;
    } else {
      url = `/timesheet/updateAdjustment`;
    }
    return new Promise((resolve, reject) => {
      ApiService.post(url, { Id: id, Adjustment: adjustment })
        .then((result) => {
          resolve(result.data);
        })
        .catch((error) => reject(error));
    });
  }

  public static async getListInDateRangeByLinemanager(
    linemanagerId: string | string[],
    teammemberId: string,
    startDate: Date,
    endDate: Date
  ): Promise<Timesheet[]> {
    return new Promise((resolve, reject) => {
      const url = "/timesheet/GetDateRangeListByLinemanager";
      endDate.setHours(23);
      ApiService.post(url, { LinemanagerId: linemanagerId, teammemberId, startDate, endDate })
        .then((result) => {
          if (result.data) {
            result.data.forEach((element: any) => {
              element.Date = Utils.stringToDate(element.Date);
            });
            resolve(result.data as Timesheet[]);
          } else {
            resolve([]);
          }
        })
        .catch((error) => reject(error));
    });
  }

  public static async getListInDateRange(teammemberId: string | string[], startDate: Date, endDate: Date): Promise<Timesheet[]> {
    return await this.getListInDateRangeBase(teammemberId, "", startDate, endDate);
  }

  public static async getListInDateRangeByTeammemberAndCommitment(
    teammemberId: string,
    commitmentId: string,
    startDate: Date,
    endDate: Date
  ): Promise<Timesheet[]> {
    return await this.getListInDateRangeBase(teammemberId, commitmentId, startDate, endDate);
  }

  private static async getListInDateRangeBase(
    teammemberId: string | string[],
    commitmentId: string | string[],
    startDate: Date,
    endDate: Date
  ): Promise<Timesheet[]> {
    return new Promise((resolve, reject) => {
      const url = "/timesheet/GetDateRangeList";

      ApiService.post(url, { TeammemberId: teammemberId, commitmentId, startDate, endDate })
        .then((result) => {
          if (result.data) {
            result.data.forEach((element: any) => {
              element.Date = Utils.stringToDate(element.Date);
            });
            resolve(result.data as Timesheet[]);
          } else {
            resolve([]);
          }
        })
        .catch((error) => reject(error));
    });
  }

  public static delete(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      ApiService.delete(`/timesheet/remove/${id}`)
        .then((result) => {
          resolve();
        })
        .catch((error) => reject(error));
    });
  }

  public static getByCustomer(customerId: string, startDate: Date | null = null, endDate: Date | null = null): Promise<Timesheet[]> {
    return new Promise((resolve, reject) => {
      const fromDate = Utils.addTimezoneOffsetToStartDate(startDate);
      const toDate = Utils.addTimezoneOffsetToEndDate(endDate);

      ApiService.post("/reports/analyticsreport", { CustomerId: customerId, startDate: fromDate, endDate: toDate })
        .then((timesheetResult) => {
          if (!timesheetResult.data) {
            resolve([]);
          } else {
            const items = timesheetResult.data.map((tdoc: any) => {
              let retVal;
              tdoc.Date = Utils.stringToDate(tdoc.Date);
              const item = tdoc as Timesheet;
              if (
                (item.Date && fromDate && toDate && fromDate <= item.Date && toDate > item.Date) ||
                (item.Date && fromDate && fromDate <= item.Date) ||
                (item.Date && toDate && toDate > item.Date)
              ) {
                retVal = item;
                return retVal;
              }
            });
            resolve(items);
          }
        })
        .catch((timesheetError) => {
          reject("timesheet has error -> " + timesheetError);
        });
    });
  }

  public static TimesheetReviewResultForTeammember(allTimesheets: Timesheet[], commits: RepositoryTeammemberCommit[]): string[] {
    const result = [] as string[];
    if (allTimesheets && allTimesheets.length) {
      const groupeByCommitmentId = allTimesheets.reduce((grouped: any, item: any) => {
        const key = item.CommitmentId;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(item);
        return grouped;
      }, {});

      for (const cmId in groupeByCommitmentId) {
        const timesheets = allTimesheets.filter((x) => x.CommitmentId === cmId);
        if (timesheets && timesheets.length > 0) {
          const selectedCommitment = timesheets[0].Commitment;

          const groupeByIssueNo = timesheets.reduce((grouped: any, item: any) => {
            const key = item.IssueNo;
            if (!grouped[key]) {
              grouped[key] = [];
            }
            grouped[key].push(item);
            return grouped;
          }, {});

          const groupeByDate = timesheets.reduce((grouped: any, item: any) => {
            const key = item.Date ? item.Date.toISOString().split("T")[0] : "";
            if (!grouped[key]) {
              grouped[key] = [];
            }
            grouped[key].push(item);
            return grouped;
          }, {});

          //- if sum(timesheet) for one task > 20 hours - return
          // Recommendation: Long Task - Description: XX hours spent on one task is not advisable  Suggested Improvement: Tasks should be broken down to smaller pieces
          var effortIssues = "";
          for (const issueNo in groupeByIssueNo) {
            const issueData = groupeByIssueNo[issueNo];
            const totalHour = issueData.reduce((total: any, item: any) => total + item.Effort, 0);
            if (totalHour > 20) {
              effortIssues += `${effortIssues.length > 0 ? ", " : ""}${issueNo}`;
            }
          }
          if (effortIssues) {
            result.push(
              `- Long Task - More than 20 hours spent on one task(s) [${effortIssues}] is not advisable. Suggested Improvement: Tasks should be broken down to smaller pieces`
            );
          }
          //- if daily variance/deviation > 3 -
          // Recommendation: incosistent days - Description: Incosistencies between the hours of work in different days - Suggested Improvement: It's better to allocate around 8 hours a day
          var dateIssues = "";
          if (selectedCommitment?.HoursPerWeekMax && selectedCommitment.HoursPerWeekMin) {
            const maxHoursPerDay = selectedCommitment?.HoursPerWeekMax / 5;
            const minHoursPerDay = selectedCommitment?.HoursPerWeekMin / 5;
            for (const date in groupeByDate) {
              const dateData = groupeByDate[date];
              const totalHour = dateData.reduce((total: any, item: any) => total + item.Effort, 0);
              if (totalHour > maxHoursPerDay || totalHour < minHoursPerDay) {
                dateIssues += `${dateIssues.length > 0 ? ", " : ""}${date}`;
              }
            }
            if (dateIssues) {
              result.push(
                `- Inconsistent days -  Incosistencies between the hours of work in [${dateIssues}] - Suggested Improvement: It's better to allocate around 8 hours a day`
              );
            }
          }

          //- Average Description length < 20
          let poorDescriptionsCount = 0;
          let noEpicCount = 0;
          timesheets.forEach((timesheet) => {
            if (timesheet.Description.length < 20) {
              poorDescriptionsCount++;
            }

            if (timesheet.Epic?.toLocaleLowerCase() == "n/a") {
              noEpicCount++;
            }
          });
          if (poorDescriptionsCount > 3) {
            result.push(`- Description of ${poorDescriptionsCount} tasks are poor, Please ask teammember to use better descriptions`);
          }
          //- No Epic
          if (noEpicCount > 3) {
            result.push(`- ${noEpicCount} task(s) have no epics, Please ask teammember to learn about epics and use them on Timesheet insertion`);
          }

          if (commits && commits.length > 0) {
            //- if line of code changed < time logged for coding * 10 -
            // Recommendation: Time/Code ratio - Description: Lines of codes changed is proportionally too little for the time logged - Suggestion: Coding efficiency must be improved, or better allocation of tasks leveraged to improve on the number of lines changed

            //- Number of commits that cannot be linked to tickets
            let noneLinkedCommitMessages = [] as string[];
            commits.forEach((commit: any) => {
              const has = commit.Hash;
              const existCommit = (timesheets as any[]).find((timesheet) => {
                const timsheetCommits = timesheet.Commits;
                if (timsheetCommits) {
                  return timsheetCommits.find((x: any) => x.hash == has);
                } else {
                  return false;
                }
              });
              if (!existCommit) {
                noneLinkedCommitMessages.push(commit.MessageShort);
              }
            });
            if (noneLinkedCommitMessages && noneLinkedCommitMessages.length) {
              result.push(
                `- ${noneLinkedCommitMessages.length} commits cannot be linked to the tasks <br> ${noneLinkedCommitMessages.join("   <br> ")}`
              );
            }
          } else {
            result.push(`- There hasn't been a commit fetched for this team member. Please check for any issues.`);
          }
        }
      }
    }
    return result;
  }
}
